/**
 * Country and language selection on the segmentless .com entry page.
 *
 * Shares the context switcher's store: the ~156 delivery countries with their
 * titles per language and their reachable languages are the same visitor-
 * independent payload the header flyout fetches.
 *
 * The geo answer is fetched here instead of through the store, because this page
 * uses it differently: it reads the plain `country` to preselect the fields,
 * where the header offers the `hint` as something to click. The hint is no use
 * here — it stays null whenever the visitor already sits in the geolocated
 * context, which on a page without any context would drop every second visitor.
 */

import {
    countryTitles,
    pinnedCountryList,
    otherCountryList,
    reachableLanguageList,
    languageOptionList,
    languageForCountry,
} from './contextCountries.js';
import { getJson } from '../shell/request.js';

document.addEventListener('alpine:init', () => {
    Alpine.data('countryEntryForm', ({ dataUrl, geoHintUrl, languageIds, pageLanguage }) => ({
        country: '',
        language: '',
        geoCountry: null,
        geoPending: true,
        // the country list is the page's only way into the shop, so a failed
        // request needs a visible state instead of a spinner that never stops
        failed: false,
        // a preselect arriving after the visitor already chose would overwrite them
        touched: false,

        init() {
            this.$store.contextSwitcher.dataUrl = dataUrl;
            this.loadCountries();
            this.loadGeoCountry(geoHintUrl);
        },

        get ready() {
            return this.$store.contextSwitcher.data !== null;
        },

        /** Both requests feed the fields, so either one still running keeps the spinner. */
        get loading() {
            return !this.failed && (!this.ready || this.geoPending);
        },

        get titlesByIso() {
            return countryTitles(this.$store.contextSwitcher.data, this.language, pageLanguage);
        },

        get pinnedCountries() {
            return pinnedCountryList(this.$store.contextSwitcher.data, this.titlesByIso);
        },

        get otherCountries() {
            return otherCountryList(this.$store.contextSwitcher.data, this.titlesByIso);
        },

        get reachableLanguages() {
            return reachableLanguageList(this.$store.contextSwitcher.data, this.country);
        },

        get languageOptions() {
            return languageOptionList(this.$store.contextSwitcher.data, this.reachableLanguages);
        },

        get languageId() {
            return languageIds[this.language] ?? '';
        },

        get canSubmit() {
            return this.country !== '' && this.language !== '';
        },

        pickCountry(iso) {
            this.touched = true;
            this.country = iso;
            this.language = languageForCountry(this.reachableLanguages, this.language);
        },

        pickLanguage(abbr) {
            this.touched = true;
            this.language = abbr;
        },

        /** Retryable: the store keeps a failed attempt out of its cache. */
        async loadCountries() {
            this.failed = false;
            await this.$store.contextSwitcher.load();
            this.failed = !this.ready;
            if (!this.failed) {
                this.applyGeoCountry();
            }
        },

        loadGeoCountry(url) {
            getJson(url)
                .then((data) => {
                    this.geoCountry = data.country;
                    this.applyGeoCountry();
                })
                .catch((error) => {
                    console.error('[countryEntry] loading the geo country failed', error);
                })
                .finally(() => {
                    this.geoPending = false;
                });
        },

        /**
         * Applied by whichever of the two requests finishes last, and again after a
         * retry — the deliverable countries and their languages only exist once the
         * country list is in.
         */
        applyGeoCountry() {
            if (this.touched || !this.ready || !this.geoCountry) {
                return;
            }

            // A country the shop does not deliver to has no option to select and no
            // reachable language; preselecting it would arm the button for a dead target.
            if (!(this.geoCountry in this.titlesByIso)) {
                return;
            }

            this.country = this.geoCountry;
            this.language = this.preferredLanguage();
        },

        /**
         * What the visitor wants to read beats what the shop sells there by default —
         * as long as it is offered in that country.
         */
        preferredLanguage() {
            const reachable = this.reachableLanguages;
            const browserLanguage = (navigator.language || '').slice(0, 2).toLowerCase();

            return reachable.includes(browserLanguage) ? browserLanguage : (reachable[0] ?? '');
        },
    }));
});
